import { ExternalRecordLink } from "components/home-care-leads/send-to-partner/external-record-link";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import {
    ResultDetail,
    RunStatusIcon,
    runStatusTextClassName,
    runTitleClassName,
} from "components/workflow/workflow-run-status";
import React, { useMemo } from "react";
import { useNow } from "store/selectors/hooks";
import {
    entryIconStateOf,
    entryTimeOf,
    entryTitleOf,
    headlineOf,
    historyDetailOf,
    iconStateOf,
    PartnerSyncCurrent,
    PartnerSyncHistoryEntry,
    PartnerSyncStatus,
    summaryDetailOf,
    syncNowLabelOf,
} from "./partner-sync-display";

export interface PartnerSyncNow {
    run: () => void;
    isPending: boolean;
    error: string | null;
}

export interface PartnerSyncProps {
    partnerName: string;
    url: string | null;
    status: PartnerSyncStatus;
    syncNow: PartnerSyncNow;
    now: Date;
}

export const usePartnerSyncClock = (dataUpdatedAt: number): Date => {
    const tick = useNow();
    return useMemo(
        () => new Date(Math.max(tick.getTime(), dataUpdatedAt)),
        [tick, dataUpdatedAt],
    );
};

export const PartnerSyncState: React.FC<{ current: PartnerSyncCurrent; now: Date }> = ({
    current,
    now,
}) => (
    <span className="inline-flex items-center gap-2">
        <RunStatusIcon state={iconStateOf(current)} />
        <span>{headlineOf(current, now)}</span>
    </span>
);

export const PartnerSyncSummary: React.FC<{
    current: PartnerSyncCurrent | null;
    partnerName: string;
    now: Date;
}> = ({ current, partnerName, now }) => {
    if (!current) {
        return <p className="text-sm text-gray-700">Nothing has synced yet.</p>;
    }
    return (
        <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">
                <PartnerSyncState current={current} now={now} />
            </div>
            <p className="pl-7 text-sm text-gray-700">
                {summaryDetailOf(current, partnerName, now)}
            </p>
        </div>
    );
};

const PartnerSyncHistoryRow: React.FC<{
    entry: PartnerSyncHistoryEntry;
    current: PartnerSyncCurrent | null;
    partnerName: string;
}> = ({ entry, current, partnerName }) => {
    const state = entryIconStateOf(entry, current);
    return (
        <li className="py-2 space-y-1">
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                <div className="flex items-start gap-2">
                    <RunStatusIcon state={state} />
                    <span className={runTitleClassName(state)}>
                        {entryTitleOf(entry, partnerName, current)}
                    </span>
                </div>
                <div className="pl-7 sm:pl-0">
                    <span className={runStatusTextClassName}>{entryTimeOf(entry)}</span>
                </div>
            </div>
            <ResultDetail detail={historyDetailOf(entry, current)} className="pl-7" />
        </li>
    );
};

const PartnerSyncHistory: React.FC<{
    history: PartnerSyncHistoryEntry[];
    current: PartnerSyncCurrent | null;
    partnerName: string;
}> = ({ history, current, partnerName }) => {
    if (history.length === 0) {
        return null;
    }
    return (
        <div className="space-y-1">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                History
            </h3>
            <ul className="divide-y divide-gray-100 border-y border-gray-100">
                {history.map(entry => (
                    <PartnerSyncHistoryRow
                        key={entry.id}
                        entry={entry}
                        current={current}
                        partnerName={partnerName}
                    />
                ))}
            </ul>
        </div>
    );
};

export const PartnerSyncDetails: React.FC<PartnerSyncProps> = ({
    partnerName,
    url,
    status,
    syncNow,
    now,
}) => (
    <div className="space-y-4">
        <PartnerSyncSummary
            current={status.current}
            partnerName={partnerName}
            now={now}
        />
        {syncNow.error ? <InlineBanner type="error">{syncNow.error}</InlineBanner> : null}
        <div className="flex flex-wrap items-center gap-3">
            {url ? <ExternalRecordLink url={url} partnerName={partnerName} /> : null}
            <div className="ml-auto">
                <PartnerSyncNowButton current={status.current} syncNow={syncNow} />
            </div>
        </div>
        <PartnerSyncHistory
            history={status.history}
            current={status.current}
            partnerName={partnerName}
        />
    </div>
);

export const PartnerSyncNowButton: React.FC<{
    current: PartnerSyncCurrent | null;
    syncNow: PartnerSyncNow;
}> = ({ current, syncNow }) => (
    <PrimaryButton onClick={syncNow.run} disabled={syncNow.isPending}>
        {syncNowLabelOf(current)}
    </PrimaryButton>
);
